const React = window.React;
const { useEffect, useCallback, useRef, useState } = React;

type PaywallModalCenteredProps = {
    isOpen: boolean;
    onClose: () => void;
    children: React.ReactNode;
};

export function PaywallModalCentered({ isOpen, onClose, children }: PaywallModalCenteredProps) {
    // isVisible gates rendering; stays true during the exit animation so the DOM exists
    const [isVisible, setIsVisible] = useState(false);
    const overlayRef = useRef<HTMLDivElement>(null);
    const cardRef = useRef<HTMLDivElement>(null);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
    }, [onClose]);

    // Mount the DOM as soon as isOpen becomes true
    useEffect(() => {
        if (isOpen) setIsVisible(true);
    }, [isOpen]);

    // Run enter / exit animation once the DOM is present
    useEffect(() => {
        if (!isVisible) return;

        const anime = (window as any).anime;
        const overlay = overlayRef.current;
        const card = cardRef.current;
        if (!overlay || !card) return;

        if (isOpen) {
            if (!anime) {
                overlay.style.opacity = '1';
                card.style.opacity = '1';
                return;
            }
            anime({ targets: overlay, opacity: [0, 1], duration: 250, easing: 'easeOutQuad' });
            anime({ targets: card, opacity: [0, 1], translateY: [20, 0], duration: 300, easing: 'easeOutCubic' });
        } else {
            if (!anime) {
                setIsVisible(false);
                return;
            }
            anime({ targets: card, opacity: 0, translateY: 12, duration: 200, easing: 'easeInQuad' });
            anime({
                targets: overlay,
                opacity: 0,
                duration: 250,
                easing: 'easeInQuad',
                complete: () => setIsVisible(false),
            });
        }
    }, [isOpen, isVisible]);

    // Hold scroll lock for the full duration including exit animation.
    // position: fixed on body freezes the layout so the scrollbar disappearing
    // never causes a width change — no padding math needed.
    useEffect(() => {
        if (!isVisible) return;

        const scrollY = window.scrollY;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        // CSS custom property lets stylesheets compensate any element they own
        document.documentElement.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);

        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollY}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';

        // Compensate fixed direct children of <body> (nav bars, admin bars, banners)
        const fixedEls = Array.from(document.body.children).filter(
            (el) => getComputedStyle(el).position === 'fixed'
        ) as HTMLElement[];
        const savedPaddings = fixedEls.map((el) => el.style.paddingRight);
        fixedEls.forEach((el) => {
            const current = parseFloat(getComputedStyle(el).paddingRight) || 0;
            el.style.paddingRight = `${current + scrollbarWidth}px`;
        });

        return () => {
            document.documentElement.style.removeProperty('--scrollbar-width');
            document.body.style.overflow = '';
            document.body.style.position = '';
            document.body.style.top = '';
            document.body.style.left = '';
            document.body.style.right = '';
            // Removing position:fixed snaps back to scrollY; immediately go to top
            // (treatment-A always closes to top, and the exit animation has already played)
            window.scrollTo(0, 0);
            fixedEls.forEach((el, i) => {
                el.style.paddingRight = savedPaddings[i];
            });
        };
    }, [isVisible]);

    // Keyboard handler only while open
    useEffect(() => {
        if (!isOpen) return;
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, handleKeyDown]);

    if (!isVisible) return null;

    const paddingClasses = 'p-3 p-lg-4 p-xl-5';

    return (
        <div
            ref={overlayRef}
            role="dialog"
            aria-modal="true"
            onClick={onClose}
            style={{
                position: 'fixed',
                inset: 0,
                zIndex: 9999,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                backgroundColor: 'rgba( var(--bs-body-color-rgb), 0.2)',
                opacity: 0,
            }}
        >
            <div
                ref={cardRef}
                onClick={(e) => e.stopPropagation()}
                style={{
                    position: 'relative',
                    maxWidth: '560px',
                    width: '100%',
                    margin: '0 16px',
                    borderRadius: '28px',
                    backgroundColor: 'var(--bs-body-bg)',
                    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                    maxHeight: '90vh',
                    overflowY: 'auto',
                    opacity: 0,
                }}
            >
                <div className={`d-flex justify-content-between align-items-center ${paddingClasses} `}>
                    <div>
                        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="20" cy="20" r="20" fill="#FFFF4C"></circle>
                            <path d="M25.1856 16.5742V13.0557C25.1856 10.1415 22.8635 7.77789 20.0004 7.77789C17.1373 7.77789 14.8152 10.1415 14.8152 13.0557V16.5742H12.2227V28.889H27.7782V16.5742H25.1856ZM16.5436 13.0557C16.5436 11.1152 18.094 9.53715 20.0004 9.53715C21.9069 9.53715 23.4572 11.1152 23.4572 13.0557V16.5742H16.5436V13.0557ZM21.1115 25.6668C21.1115 26.2191 20.6638 26.6668 20.1115 26.6668H19.8893C19.337 26.6668 18.8893 26.2191 18.8893 25.6668V22.2223H21.1115V25.6668Z" fill="#2A2628"></path>
                            <path d="M22.2228 21.1113C22.2228 22.3386 21.2278 23.3335 20.0005 23.3335C18.7732 23.3335 17.7783 22.3386 17.7783 21.1113C17.7783 19.884 18.7732 18.8891 20.0005 18.8891C21.2278 18.8891 22.2228 19.884 22.2228 21.1113Z" fill="#FFFF4C"></path>
                        </svg>
                    </div>
                    <button
                        aria-label="Close"
                        onClick={onClose}
                        className="btn btn-link text-decoration-none "
                    >
                        <svg width="23" height="27" viewBox="0 0 23 27" fill="none" className="h3 mb-0 text-hover-primary" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12.4002 12.8398L22.8002 26.3198H21.1202L11.4002 13.6798L1.60022 26.3198H0.000214905L10.4002 12.8398L0.360215 -0.00017786H2.04022L11.4402 11.9598L20.8002 -0.00017786H22.4402L12.4002 12.8398Z" fill="currentColor" />
                        </svg>
                    </button>
                </div>
                <div className={`${paddingClasses} grow-1 d-flex flex-column align-items-center text-center justify-content-center gap-4`}>

                    {/* Adweek Logo */}
                    <svg id="adw-logo" style={{ maxWidth: '160px', height: 'auto' }} className="svg-logo d-flex" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 654.76 260.09" fill="var(--bs-heading-color)">
                        <g id="logo-group">
                            <path d="M74.42,0h38.79l-17.02,260.09h-36.42l3.56-32.86h-18.61l-7.92,32.86H0L74.42,0ZM52.26,194.77h14.65l13.06-116.78-27.71,116.78Z"></path>
                            <path d="M191.6,0c29.69,0,45.13,15.83,38.79,50.28l-28.11,159.54c-6.33,36.42-26.52,50.28-56.61,50.28h-44.34L147.26,0h44.34ZM192,50.28c1.58-9.5-.4-15.44-11.48-15.44h-1.19l-33.25,190.41h1.19c9.11,0,14.65-4.75,16.63-15.44l28.11-159.54Z"></path>
                            <path d="M363.41,0h39.98l-72.84,260.09h-42.36l17.02-159.93-39.19,159.93h-42.36L242.67,0h39.98l-24.94,190.41L302.84,0h40.38l-21.77,190.41L363.41,0Z"></path>
                            <path d="M474.25,34.84h-27.31l-13.46,76.01h20.19l-5.94,34.04h-20.19l-14.25,80.36h27.71l-6.33,34.84h-66.11L414.47,0h66.11l-6.33,34.84Z"></path>
                            <path d="M549.86,34.84h-27.32l-13.46,76.01h20.19l-5.94,34.04h-20.19l-14.25,80.36h27.71l-6.33,34.84h-66.11L490.08,0h66.11l-6.33,34.84Z"></path>
                            <path d="M654.76,0l-33.49,89.32-10.06,170.76h-38l11.32-108.48-13.46,30.09-13.69,78.4h-37.61L565.69,0h37.61l-17.28,96.14L618.73,0h36.02Z"></path>
                        </g>
                    </svg>

                    {children}



                </div>
                <div className={`${paddingClasses} text-center mb-5`}>
                    {/* Already a member? */}
                    {!window?.Zephr?.accessDetails?.isAuthenticated && (
                        <p className="text-center text-uppercase fs-7 fw-bold d-inline-flex align-items-baseline mb-0">
                            Already a member? &nbsp;
                            <a
                                className="btn lh-1 btn-link fs-7 fw-bold p-0"
                                onClick={() => {
                                    window.location.href = '/sign-in';
                                }}
                                href="/sign-in"
                            >
                                Sign in
                            </a>
                        </p>
                    )}
                </div>
            </div>
        </div>
    );
}
